// Iter56 — NewStockLockModal
//
// Modal to request a new stock lock. Standalone or linked to an open incident.
// Fields:
//   - Standalone vs link toggle
//   - Part number (required) + part description
//   - Reason (required)
//
// POST /api/stock-locks
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useBarcodeScanner } from "../lib/useBarcodeScanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewStockLockModal = ({ onClose, onCreated }) => {
  const addToast = useStore((s) => s.addToast);
  const [mode, setMode] = useState("standalone"); // 'standalone' | 'linked'
  const [partNumber, setPartNumber] = useState("");
  const [partDescription, setPartDescription] = useState("");
  const [reason, setReason] = useState("");
  const [quantityEstimate, setQuantityEstimate] = useState("");
  const [releaseReminderAt, setReleaseReminderAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [incidents, setIncidents] = useState([]);
  const [search, setSearch] = useState("");
  const [pickedIncident, setPickedIncident] = useState(null);

  // Iter57 — barcode scan for the part number field.
  const scanner = useBarcodeScanner({
    onScanned: (code) => {
      setPartNumber(code);
      addToast({ type: "success", title: "Barcode scanned", body: code });
    },
    onError: (msg) => setError(msg),
  });

  useEffect(() => {
    if (mode !== "linked") return;
    axios.get(`${API}/incidents?status_in=open,investigating,escalated`)
      .then(({ data }) => setIncidents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [mode]);

  const filtered = incidents
    .filter((i) => {
      const t = (i.title || "").toLowerCase();
      const id = (i.id || "").toLowerCase();
      const s = search.toLowerCase();
      return !s || t.includes(s) || id.includes(s);
    })
    .slice(0, 30);

  const submit = async () => {
    setError("");
    if (!partNumber.trim()) { setError("Part number is required"); return; }
    if (!reason.trim()) { setError("Reason is required"); return; }
    if (mode === "linked" && !pickedIncident) { setError("Pick an incident or switch to standalone"); return; }
    setBusy(true);
    try {
      const payload = {
        part_number: partNumber.trim(),
        part_description: partDescription.trim() || null,
        reason: reason.trim(),
        incident_id: mode === "linked" ? pickedIncident.id : null,
        quantity_estimate: quantityEstimate === "" ? null : Math.max(0, parseInt(quantityEstimate, 10) || 0),
        release_reminder_at: releaseReminderAt
          ? new Date(releaseReminderAt).toISOString()
          : null,
      };
      const { data } = await axios.post(`${API}/stock-locks`, payload);
      addToast({ type: "success", title: "Stock lock requested", body: data.id });
      onCreated?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Submit failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-3"
      style={{ background: "rgba(0,0,0,0.65)", zIndex: 400 }}
      onClick={onClose}
      data-testid="stock-lock-modal-overlay"
    >
      <div
        className="rounded-md p-4"
        style={{
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.18)",
          width: "min(620px, 96vw)", maxHeight: "92vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="stock-lock-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)" }}>
            New Stock Lock Request
          </span>
          <button onClick={onClose} style={{ color: "#9aa1b3", fontSize: 22, cursor: "pointer", background: "transparent", border: "none" }} data-testid="stock-lock-modal-close">×</button>
        </div>

        <div className="mb-3 flex gap-2">
          <ModePill active={mode === "standalone"} onClick={() => setMode("standalone")} testId="stock-lock-mode-standalone">Standalone</ModePill>
          <ModePill active={mode === "linked"} onClick={() => setMode("linked")} testId="stock-lock-mode-linked">Link to incident</ModePill>
        </div>

        {mode === "linked" && (
          <div className="mb-3 rounded-md p-2" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid="stock-lock-incident-picker">
            <input
              type="text" placeholder="Search incidents by id or title…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1.5 rounded outline-none mb-2"
              style={inputStyle()}
              data-testid="stock-lock-incident-search"
            />
            <div className="max-h-40 overflow-y-auto space-y-1">
              {filtered.map((inc) => (
                <button
                  key={inc.id} type="button"
                  onClick={() => setPickedIncident(inc)}
                  className="w-full text-left px-2 py-1.5 rounded"
                  style={{
                    background: pickedIncident?.id === inc.id ? "rgba(24,95,165,0.20)" : "var(--bg-base, #111420)",
                    border: pickedIncident?.id === inc.id ? "1px solid rgba(24,95,165,0.55)" : "1px solid rgba(255,255,255,0.08)",
                    cursor: "pointer",
                  }}
                  data-testid={`stock-lock-incident-row-${inc.id}`}
                >
                  <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9ec3ec" }}>{inc.id}</span>
                  <span className="ml-2" style={{ fontFamily: "Syne, sans-serif", fontSize: 12, color: "var(--text, #f0f2f8)" }}>{inc.title}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", padding: "8px 4px" }}>
                  No open incidents match the search.
                </div>
              )}
            </div>
          </div>
        )}

        <Field label="Part number *">
          <div className="flex gap-2 items-stretch" data-testid="stock-lock-part-number-row">
            <input
              type="text" value={partNumber} onChange={(e) => setPartNumber(e.target.value)}
              className="flex-1 px-2 py-1.5 rounded outline-none"
              style={inputStyle()}
              placeholder="51 41 7 234 091"
              data-testid="stock-lock-part-number"
            />
            {scanner.supported && (
              <button
                type="button"
                onClick={scanner.scan}
                disabled={scanner.busy}
                className="px-2.5 py-1.5 rounded"
                style={{
                  background: "rgba(78,208,132,0.10)",
                  border: "1px solid rgba(78,208,132,0.40)",
                  color: "#9aebc2",
                  fontFamily: "DM Mono, monospace", fontSize: 10, fontWeight: 700, letterSpacing: "0.4px",
                  cursor: scanner.busy ? "wait" : "pointer", whiteSpace: "nowrap",
                }}
                data-testid="stock-lock-part-scan"
                title="Scan barcode using device camera"
              >
                {scanner.busy ? "📷 Scanning…" : "📷 Scan"}
              </button>
            )}
          </div>
        </Field>
        <Field label="Part description (optional)">
          <input
            type="text" value={partDescription} onChange={(e) => setPartDescription(e.target.value)}
            className="w-full px-2 py-1.5 rounded outline-none"
            style={inputStyle()}
            placeholder="e.g. Door panel — front left"
            data-testid="stock-lock-part-description"
          />
        </Field>
        <Field label="Reason for lock *">
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            className="w-full px-2 py-1.5 rounded outline-none"
            style={{ ...inputStyle(), resize: "vertical" }}
            placeholder="e.g. Suspected dimensional non-conformance from supplier lot LOT-23456"
            data-testid="stock-lock-reason"
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Estimated quantity (pcs)">
            <input
              type="number" min="0" value={quantityEstimate}
              onChange={(e) => setQuantityEstimate(e.target.value)}
              className="w-full px-2 py-1.5 rounded outline-none"
              style={inputStyle()}
              placeholder="e.g. 480"
              data-testid="stock-lock-quantity-estimate"
            />
          </Field>
          <Field label="Release reminder / deadline">
            <input
              type="datetime-local" value={releaseReminderAt}
              onChange={(e) => setReleaseReminderAt(e.target.value)}
              className="w-full px-2 py-1.5 rounded outline-none"
              style={inputStyle()}
              data-testid="stock-lock-release-reminder"
            />
          </Field>
        </div>

        {error && (
          <div className="mt-2 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "DM Mono, monospace", fontSize: 11 }} data-testid="stock-lock-modal-error">
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button" onClick={onClose}
            className="px-3 py-2 rounded-md"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            data-testid="stock-lock-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button" onClick={submit} disabled={busy}
            className="px-3 py-2 rounded-md"
            style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1, border: "none" }}
            data-testid="stock-lock-modal-submit"
          >
            {busy ? "Submitting…" : "Request lock"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ModePill = ({ active, onClick, children, testId }) => (
  <button
    type="button" onClick={onClick}
    className="px-3 py-1.5 rounded"
    style={{
      background: active ? "rgba(24,95,165,0.16)" : "transparent",
      border: `1px solid ${active ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
      color: active ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
      fontFamily: "DM Mono, monospace", fontSize: 11, letterSpacing: "0.5px",
      cursor: "pointer",
    }}
    data-testid={testId}
  >
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div className="mt-3">
    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);

const inputStyle = (fontSize = 12) => ({
  background: "var(--bg3, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace",
  fontSize,
});
